import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useGetTariffsQuery } from '@/app/Pricing/api/pricingApi';
import {
  buildMentoringPlansWithPrices,
  getTrackerAddonId,
  getTrackerDailyPrice,
  getWalletAddonId,
} from '@/app/Pricing/utils/addonPricing';
import { buildPlansWithPrices } from '@/app/Pricing/utils/tariffPricing';
import type { MentoringPlan, PlanId, PlanWithPrices } from '@/app/Pricing/types';
import type {
  PricingAddon,
  RealAndLegal,
  Tariffs,
} from '@/app/Pricing/types/pricingApiTypes';

type PricingTariffsContextValue = {
  plans: Record<PlanId, PlanWithPrices>;
  tariffs: Tariffs;
  mentoringPlans: MentoringPlan[];
  apiAddons: PricingAddon[];
  realAndLegal: RealAndLegal | null;
  remainingAmount: number;
  trackerAddonId?: string;
  walletAddonId?: string;
  trackerDailyPrice: number;
  isLoading: boolean;
  isError: boolean;
};

const CONTEXT_KEY = '__mizfaPricingTariffsContext__';

type PricingTariffsGlobal = typeof globalThis & {
  [CONTEXT_KEY]?: ReturnType<
    typeof createContext<PricingTariffsContextValue | null>
  >;
};

// Keep the same context instance across Vite HMR invalidations of this module
// (triggered when pricing-data.json / dependent utils change).
const PricingTariffsContext =
  (globalThis as PricingTariffsGlobal)[CONTEXT_KEY] ??
  createContext<PricingTariffsContextValue | null>(null);

(globalThis as PricingTariffsGlobal)[CONTEXT_KEY] = PricingTariffsContext;

export function PricingTariffsProvider({ children }: { children: ReactNode }) {
  const { data, isLoading, isError } = useGetTariffsQuery();
  const addons = data?.addons ?? [];
  const tariffs = data?.tariffs ?? {};

  const plans = useMemo(
    () => buildPlansWithPrices(tariffs),
    [tariffs],
  );
  const mentoringPlans = useMemo(
    () => buildMentoringPlansWithPrices(addons),
    [addons],
  );
  const trackerDailyPrice = useMemo(
    () => getTrackerDailyPrice(addons),
    [addons],
  );
  const trackerAddonId = useMemo(
    () => getTrackerAddonId(addons),
    [addons],
  );
  const walletAddonId = useMemo(
    () => getWalletAddonId(addons),
    [addons],
  );

  return (
    <PricingTariffsContext.Provider
      value={{
        plans,
        tariffs,
        mentoringPlans,
        apiAddons: addons,
        realAndLegal: data?.real_and_legal ?? null,
        remainingAmount: data?.remaining_amount ?? 0,
        trackerAddonId,
        walletAddonId,
        trackerDailyPrice,
        isLoading,
        isError,
      }}
    >
      {children}
    </PricingTariffsContext.Provider>
  );
}

export function usePricingTariffs(): PricingTariffsContextValue {
  const context = useContext(PricingTariffsContext);
  if (!context) {
    throw new Error(
      'usePricingTariffs must be used within PricingTariffsProvider',
    );
  }
  return context;
}
